/**
 * Type definitions for the Position Opener / Entry feature.
 *
 * Mirrors the unwinding types module but inverted for BUY-side semantics:
 * - Unwinding "contractsToSell" → entry "contractsToBuy"
 * - Unwinding "contractsSoldKalshi/Polymarket" → entry "contractsBoughtKalshi/Polymarket"
 *
 * The entry engine evaluates whether the SUM of both legs' ASK prices is
 * below a user-configured threshold AND below the absolute hard cap of
 * $0.999 (anything >= $1 is unprofitable by construction — buying both
 * sides of a binary market for >= $1 cannot yield arbitrage).
 */

export interface TopOfBook {
  price: number;
  size: number;
}

/**
 * Hard upper bound for the sum of ASKs at which an entry order may be
 * placed. Enforced in three places on the frontend (input validator,
 * confirmation banner, Activate button disabled state) and again on the
 * backend. There is intentionally no admin override.
 */
export const ENTRY_HARD_CAP_GROSS = 0.999;

/**
 * Default target TNA percent suggested for new entry rules. Operators
 * adjust per pair — this is just the seeded value when creating a rule.
 */
export const DEFAULT_TARGET_TNA_PERCENT = 40;

export interface EntrySimulationBreakdown {
  rawTakerAsk: number;
  netTakerAsk: number;
  takerFeeRate: number;
  takerFeeAmount: number;
  takerPlatform: string;
  targetTna: number;
  daysToExpiry: number;
  effectiveDays: number;
  maxBuyPrice: number;
  makerBidTopPrice: number;
  makerBidTopSize: number;
  joinedExistingLevel: boolean;
  requestedSize: number;
  takerLiquidityCap: number;
  pairCap: number;
  effectiveSize: number;
  makerPrice: number;
  makerFeeRate: number;
  makerFeeAmount: number;
  netMakerPrice: number;
  achievedTna: number;
  unplaceableNoMillis?: boolean;
}

export interface EntrySimulationResult {
  makerPrice: number;
  takerPrice: number;
  tna: number;
  effectiveSize: number;
  breakdown?: EntrySimulationBreakdown;
}

export type EntryRuleStatus = 'active' | 'paused' | 'completed' | 'cancelled';
export type EntryRuleType = 'tna' | 'sum_of_sides';
export type EntryStrategy = 'maker-first' | 'taker-taker';

export interface EnrichedEntryStatus {
  ruleId: string;
  pairId?: string;
  state: string;
  makerPlatform: 'kalshi' | 'polymarket';
  makerOrderId: string | null;
  makerPrice: number | null;
  makerFilledQty: number;
  makerFilledPrice: number;
  counterOrderId: string | null;
  counterFilledQty: number;
  lastStateChange: string;
  errorMessage: string | null;
  pairName: string;
  expiryDate: string;
  daysToExpiry: number;
  polyBid: TopOfBook | null;
  polyAsk: TopOfBook | null;
  kalshiBid: TopOfBook | null;
  kalshiAsk: TopOfBook | null;
  /** Sum of both legs' ASK prices — the entry-side analogue of the
   *  unwinder's TT. When this drops below the rule's threshold AND below
   *  ENTRY_HARD_CAP_GROSS, the engine fires. */
  currentSumOfAsks: number | null;
  simulation: {
    polyAsMaker: EntrySimulationResult | null;
    kalshiAsMaker: EntrySimulationResult | null;
  };
  kalshiConfig: {
    side: 'yes' | 'no';
    ticker: string;
    contractsToBuy: number;
    targetTnaPercent: number;
  } | null;
  polymarketConfig: {
    side: 'yes' | 'no';
    // Both outcome tokens; `tokenId` is the backend virtual derived from
    // `side` (side === 'yes' ? yesTokenId : noTokenId).
    yesTokenId: string;
    noTokenId: string;
    tokenId: string;
    conditionId: string;
    contractsToBuy: number;
    targetTnaPercent: number;
  } | null;
  maxVolumeContracts: number;
  volumePeriod: 'hour' | 'day';
  volumeUsed: number;
  volumeRemaining: number;
  /**
   * Per-`(path, leg)` "this leg hit its maxV cap and was disarmed" markers.
   * Drives the exclamation icon next to each tab's MaxV box; cleared on
   * re-arm / MaxV change. Defaults to all-false when the backend omits it.
   */
  maxVolumeHit?: {
    a: { kalshi: boolean; polymarket: boolean };
    b: { kalshi: boolean; polymarket: boolean };
  } | null;
  kalshiMarketUrl: string | null;
  polymarketMarketUrl: string | null;
  simTna?: number;
  simSize?: number;
  simMaxVolume?: number;
  appliedSimTna?: number;
  appliedSimSize?: number;
  appliedSimMaxVolume?: number;
  hasPendingSim?: boolean;

  // ── Two-path market data (BUY side) ─────────────────────────────────────
  // A prediction-market arb has two entry paths; the enricher emits
  // both sides' quotes for both platforms so the row can show both
  // sums while market data + sim cells track the lower-sum / committed
  // path. See position-opener's `EnrichedRuleState` for the contract.
  polyYesBid?: TopOfBook | null;
  polyYesAsk?: TopOfBook | null;
  polyNoBid?: TopOfBook | null;
  polyNoAsk?: TopOfBook | null;
  kalshiYesBid?: TopOfBook | null;
  kalshiYesAsk?: TopOfBook | null;
  kalshiNoBid?: TopOfBook | null;
  kalshiNoAsk?: TopOfBook | null;
  /** Gross ask sum for Path A (poly_yes_ask + kalshi_no_ask). */
  sumPathA?: number | null;
  /** Gross ask sum for Path B (poly_no_ask + kalshi_yes_ask). */
  sumPathB?: number | null;
  /** Which path currently has the lower gross sum. */
  lowerPath?: 'a' | 'b' | null;
  /**
   * Non-null when contracts have been bought on this rule. Locks the
   * UI to the committed path: only its 2 toggles are editable and
   * only its sum/market-data render.
   */
  committedPath?: 'a' | 'b' | null;
  /** Per-leg maker sim cells (orange = taker net, blue = maker rest). */
  simPolyYesMaker?: { taker: number | null; maker: number | null } | null;
  simPolyNoMaker?: { taker: number | null; maker: number | null } | null;
  simKalshiYesMaker?: { taker: number | null; maker: number | null } | null;
  simKalshiNoMaker?: { taker: number | null; maker: number | null } | null;
  /**
   * Engine-parity sim: depth-walked VWAP taker prices + maker rest per
   * path. The maker rest is the maker leg's OWN live top-of-book bid
   * (where a BUY maker rests), so `sumPolyMk = ownBid + counterTakerAllIn`
   * is the live book sum — compared against `thresholdUsed` it surfaces
   * the gap. Computed by the same helpers the engine uses to trigger
   * orders. When present, the FE prefers these over its own client-side
   * approximations. Falls back to client math when null (legacy envelope
   * or pre-rule pair with no orderbook).
   */
  engineSim?: EngineSim | null;
  /**
   * The LIVE engine's believed position for this rule — what it thinks we
   * hold per (path, platform) right now, ahead of Mongo + the venue UIs.
   * `null` until a live envelope arrives. Powers the "Engine position" drawer.
   */
  believedPosition?: BelievedPosition | null;
  /**
   * The engine-native real-time per-pair ACT + money for this rule. `null` when
   * the pair has no rule / no live `ruleCost` envelope has arrived yet. The ACT
   * cell reads `ruleCost.kalshiNet` / `ruleCost.polyNet` off this in place of
   * the 10s REST positions poll (behind the `VITE_ACT_FROM_ENGINE` flag).
   */
  ruleCost?: RuleCost | null;
  /**
   * Per-market positions polled off the venue by this group's feed pod, under
   * the group's OWN account. The ACT / YES / NO cells prefer this over the
   * main-account-only REST `prediction_positions` map. `null` when the pod has
   * no venue source.
   */
  venuePositions?: PairVenuePositions | null;
  /**
   * Per-pair opener kill switch (mirrors backend
   * `OpeningPageState.pairEnabled`). Gates the activate button + market
   * data. Newly manual-confirmed pairs default to false.
   */
  pairEnabled?: boolean;
  /**
   * The rule's REAL armed state echoed by the engine on the WS envelope —
   * `true` = the rule is armed/trading, derived from its durable
   * `desiredArmed`/status. This is the AUTHORITATIVE source for the per-row
   * on/off switch's `checked`: it can never read ON while the rule is
   * actually paused (or vice-versa). `undefined` on envelopes from an older
   * backend (rollout) — the switch then falls back to `pairEnabled`.
   */
  ruleArmed?: boolean;
}

export interface EnginePathPrices {
  polyMakerRest: number | null;
  kalshiMakerRest: number | null;
  /**
   * The maker leg's ACTUAL resting placement price (threshold-capped),
   * vs `*MakerRest` which is the live own-bid. Can be lower than the bid
   * when the threshold binds. `null` when the engine emits no placement.
   */
  polyMakerPlace: number | null;
  kalshiMakerPlace: number | null;
  polyTakerAllInVwap: number | null;
  kalshiTakerAllInVwap: number | null;
  sumTT: number | null;
  sumPolyMk: number | null;
  sumKalshiMk: number | null;
}

export interface EngineSim {
  a: EnginePathPrices;
  b: EnginePathPrices;
  sizeUsed: number;
  thresholdUsed: number;
}

// ── Phase 6 — split-shape envelope ──────────────────────────────────────
// The new `OpeningPageState` decouples pair-scoped market data from
// rule-scoped engine state. The hook listens to `opening_state` /
// `opening_state_update` and projects these envelopes back into the
// legacy `EnrichedEntryStatus` shape so existing row components keep
// working. Phase 7 will rewrite the row components to consume the
// split shape directly and the projection will be removed.
export interface MarketState {
  pairId: string;
  pairName: string;
  expiryDate: string;
  daysToExpiry: number;
  polyYesBid: TopOfBook | null;
  polyYesAsk: TopOfBook | null;
  polyNoBid: TopOfBook | null;
  polyNoAsk: TopOfBook | null;
  kalshiYesBid: TopOfBook | null;
  kalshiYesAsk: TopOfBook | null;
  kalshiNoBid: TopOfBook | null;
  kalshiNoAsk: TopOfBook | null;
  kalshiMarketUrl: string | null;
  polymarketMarketUrl: string | null;
}

export interface OpenerRuleState {
  ruleId: string;
  state: string;
  makerPlatform: 'kalshi' | 'polymarket';
  makerOrderId: string | null;
  makerPrice: number | null;
  makerFilledQty: number;
  makerFilledPrice: number;
  counterOrderId: string | null;
  counterFilledQty: number;
  lastStateChange: string;
  errorMessage: string | null;
  kalshiConfig: EnrichedEntryStatus['kalshiConfig'];
  polymarketConfig: EnrichedEntryStatus['polymarketConfig'];
  simulation: EnrichedEntryStatus['simulation'];
  maxVolumeContracts: number;
  volumePeriod: 'hour' | 'day';
  volumeUsed: number;
  volumeRemaining: number;
  simTna?: number;
  simSize?: number;
  simMaxVolume?: number;
  appliedSimTna?: number;
  appliedSimSize?: number;
  appliedSimMaxVolume?: number;
  hasPendingSim?: boolean;
  kalshiOrderState: unknown;
  polyOrderState: unknown;
  sumPathA: number | null;
  sumPathB: number | null;
  lowerPath: 'a' | 'b' | null;
  committedPath: 'a' | 'b' | null;
  simPolyYesMaker: { taker: number | null; maker: number | null } | null;
  simPolyNoMaker: { taker: number | null; maker: number | null } | null;
  simKalshiYesMaker: { taker: number | null; maker: number | null } | null;
  simKalshiNoMaker: { taker: number | null; maker: number | null } | null;
  maxVolumeHit?: {
    a: { kalshi: boolean; polymarket: boolean };
    b: { kalshi: boolean; polymarket: boolean };
  } | null;
}

// ── Engine believed position ─────────────────────────────────────────────
// The LIVE engine's in-memory truth of what we hold per (path, platform),
// emitted on every fill / placement — ahead of the persisted Mongo state and
// the venue dashboards (both lag). Mirror of the backend wire `BelievedPosition`.
export interface PlatformBelief {
  /** Net believed contracts — opener: `starting + totalBought`; unwinder: `available`. */
  net: number;
  starting: number;
  totalBought: number;
  /** Available-to-sell (unwinder; never clamped — negative = oversold). */
  available: number;
  contractsBoughtCycle: number;
  /** Contracts bought on this leg-thread THIS ON-session (opener; resets on
   *  the leg's off→on edge). The "in memory for this session" figure. */
  sessionContractsBought: number;
  volumeExecuted: number;
  maxVolume: number;
  makerFilledQty: number;
  counterFilledQty: number;
  unhedgedPartial: number;
  supersededTotal: number;
  isMaker: boolean;
  side: 'yes' | 'no';
  makerPrice?: number | null;
  makerOrderId?: string | null;
}

export interface PathBelief {
  path: 'a' | 'b';
  kalshi: PlatformBelief;
  polymarket: PlatformBelief;
}

export interface BelievedPosition {
  direction: 'buy' | 'sell';
  bidirectional: boolean;
  /** Signed net believed Kalshi contracts across both paths (`+` net-long NO). */
  kalshiNet: number;
  /** Signed net believed Polymarket contracts across both paths (`+` net-long YES). */
  polyNet: number;
  /** `|kalshiNet − polyNet|` — cross-path hedge imbalance (0 = delta-neutral). */
  legImbalance: number;
  paths: PathBelief[];
}

/**
 * The engine-native real-time cost of ONE rule's pair — mirror of the backend
 * `RuleCost` (position-opener `wire-contracts.ts` / Rust
 * `crates/contracts/src/streams.rs`). Derived from the money accumulator, NOT
 * the execution counters. Rides the per-pair `opening_state*` envelope and is
 * the source the ACT cell reads off the socket in place of the 10s REST poll.
 * `kalshiNet` (`+` = net-long NO) / `polyNet` (`+` = net-long YES) reuse the
 * engine's net-exposure sign convention (same as `BelievedPosition`), so the
 * signed `YES − NO` ACT value is `polyNet − kalshiNet`.
 */
export interface RuleCost {
  spendUsd: number;
  feesUsd: number;
  matched: number;
  livePnlUsd: number;
  kalshiNet: number;
  polyNet: number;
  /** epoch ms. */
  computedAt: number;
}

/**
 * Per-market contract counts polled STRAIGHT off the venues by the group's own
 * feed pod, under that group's own account (prod or test). Rides the per-pair
 * `opening_state*` envelope.
 *
 * `null` (never all-zeros) whenever the pod has no venue source, so the ACT
 * column can distinguish "flat" from "unknown". The payload deliberately
 * carries NO timestamp — freshness lives only in `VenuePositionsMeta` — which
 * is what makes a whole-object deep compare safe for render equality.
 */
export interface PairVenuePositions {
  /**
   * `null` = NOT READ. The pair carries no usable Polymarket `conditionId`, so
   * the feed pod could not name it in the `market=` scope and the venue was
   * never asked about it.
   *
   * 🚨 NEVER conflate with `0`, which asserts "the account holds nothing on
   * this token". A pair holding a Polymarket-only position with Kalshi flat
   * would then compute ACT = 0 and render BALANCED while a real naked
   * Polymarket leg stands. `countsUsable` is what enforces this — its
   * `Number.isFinite` looks redundant only if this type lies.
   */
  polyYes: number | null;
  /** @see polyYes */
  polyNo: number | null;
  /** Kalshi is read account-wide and UNSCOPED, so absence there really does
   *  mean "not held" — these stay non-nullable on purpose. */
  kalshiYes: number;
  kalshiNo: number;
  /**
   * Another in-scope pair claims the same Kalshi ticker / Polymarket token, so
   * the counts below are the FULL market size repeated on BOTH rows. They
   * belong to no single pair and must never be rendered as this pair's number.
   */
  ambiguous?: boolean;
  /**
   * At least one leg reported a terminal (resolved) state on the last sweep:
   * the resolved leg is zeroed by settlement while the other still holds
   * contracts, so the imbalance is a resolution artefact, not a naked leg.
   */
  resolving?: boolean;
}

/**
 * A `PairVenuePositions` whose counts are all REAL numbers — i.e. the venue was
 * actually asked about this pair and answered.
 *
 * 🚨 The only way to get one is through `countsUsable`, which is a type guard.
 * That is deliberate: it makes "an unread Polymarket leg can never be summed as
 * zero" a fact the COMPILER enforces, instead of a discipline every future call
 * site has to remember. `venueSignedNet` takes this type for exactly that
 * reason — it is the function that would otherwise coerce `null` to `0` and
 * render a naked leg as balanced.
 */
export type ReadPairVenuePositions = PairVenuePositions & {
  polyYes: number;
  polyNo: number;
};

export type VenuePositionsSource =
  | 'venue'
  | 'unconfigured'
  | 'blocked'
  | 'error'
  /**
   * An armed FEED pod between bootstrap and its first SUCCESSFUL sweep, with
   * the skip reason in `lastError`. Distinct from `off` precisely so a feed pod
   * wedged before its first sweep cannot be mistaken for the credential-less
   * core pod, where main-account REST is the legitimate answer.
   */
  | 'starting'
  | 'off';

/**
 * Group-level provenance + freshness for `PairVenuePositions`, pushed on the
 * `venue_positions_meta` socket event by the same pod that serves the pairs.
 * Re-emitted on every sweep as a liveness heartbeat, which is what freshness
 * ages against. `source: 'off'` is the CORE gateway, which legitimately has no
 * venue source (the ACT column then falls back to the REST main-account
 * positions).
 */
export interface VenuePositionsMeta {
  source: VenuePositionsSource;
  /**
   * The two `venue_accounts` ids the feed pod actually read with. Independent:
   * a group may hold Kalshi on one account and Polymarket on another, which one
   * combined value could not express — the `account: 'prod' | 'test'` this
   * replaced could not either, and the backend stopped sending it.
   *
   * Empty string = the manifest stamped nothing (a core pod, or a feed pod from
   * before per-group accounts). Optional only for a frame from such a backend.
   */
  kalshiAccountId?: string;
  polyAccountId?: string;
  groupId: string;
  /** epoch ms of the last successful venue poll; `null` if none ever succeeded. */
  lastSuccessAt: number | null;
  lastError: string | null;
  /** Contracts excluded from the counts because they are already redeemable. */
  redeemableExcluded: number;
  /**
   * Age (ms) past which the FE must treat the counts as stale. The backend
   * derives it against its own poll cadence and is the only place it can be
   * tuned; absent only on a backend older than the field, where
   * `VENUE_STALE_MS_FALLBACK` stands in.
   */
  staleMs?: number;
}

/**
 * A received `venue_positions_meta` frame with the two timestamps freshness
 * needs: when the browser got it, and when the SERVER emitted it (`_meta`
 * envelope). `lastSuccessAt` is a server clock reading, so ageing it against
 * `Date.now()` alone silently trusts the browser clock — a browser running
 * behind the pod would read stale counts as fresh forever.
 */
export interface VenuePositionsMetaFrame {
  meta: VenuePositionsMeta;
  /** Browser epoch ms when the frame landed. */
  receivedAt: number;
  /**
   * `performance.now()` when the frame landed. The wall clock can be corrected
   * BACKWARDS while the page is open (NTP, manual change), which would freeze
   * the elapsed-since-receipt term and keep a dead feed reading fresh; the
   * monotonic clock cannot. Absent only on a frame built before this field.
   */
  receivedMono?: number;
  /** Server epoch ms off the `_meta` envelope; `null` on an older backend. */
  serverEmittedAt: number | null;
}

/** Gross contracts bought per platform for a group — mirror of `ContractsBought`. */
export interface GroupContractsBought {
  kalshi: number;
  poly: number;
  total: number;
}

/**
 * The per-GROUP live trading summary — mirror of the backend `GroupCostSummary`
 * (Rust). One opener group per pod, aggregated from the money accumulator.
 * Rides the `group_cost_update` socket event on the `/opening` gateway and is
 * the single source for the group live-summary strip (replaces the manager's 5s
 * REST `/opener-groups/:id/live-summary`). `nominalUsd` already includes fees.
 */
export interface GroupCostSummary {
  /**
   * The opener group this summary belongs to. The per-group feed pod stamps its
   * own group id; the CORE/ungrouped pod stamps `''` (empty). Mirrors the
   * backend wire field so the FE can reject a summary that isn't for the
   * selected group — e.g. the ungrouped aggregate the CORE pod emits when a
   * group's feed pod isn't Ready and the socket falls back to core.
   */
  groupId?: string;
  descalceTotal: number;
  contractsBought: GroupContractsBought;
  nominalUsd: number;
  feesUsd: number;
  matchedContracts: number;
  livePnlUsd: number;
  /** epoch ms. */
  computedAt: number;
}

export interface OpeningPageState {
  pairId: string;
  market: MarketState;
  rule: OpenerRuleState | null;
  /**
   * Per-pair opener kill switch — mirrors the backend's
   * `OpeningPageState.pairEnabled`. Newly manual-confirmed pairs default
   * to false; the row renders OFF (no market data, activate button
   * disabled) until the operator turns the switch on.
   */
  pairEnabled?: boolean;
  /**
   * The rule's REAL armed state, derived from its durable
   * `desiredArmed`/status — `true` = armed/trading. The AUTHORITATIVE source
   * for the per-row on/off switch's `checked` (NOT `pairEnabled`, which is the
   * pair-level kill switch). `undefined` on envelopes from an older backend
   * during rollout — the switch then falls back to `pairEnabled`.
   */
  ruleArmed?: boolean;
  // Always-computed (backend) path + sim payload — populated for
  // every pair regardless of whether a rule is active. The threshold
  // used for sim cells is `rule.thresholdSum` when the rule exists,
  // else `OPENER_PREVIEW_THRESHOLD` (0.95) on the backend.
  sumPathA?: number | null;
  sumPathB?: number | null;
  lowerPath?: 'a' | 'b' | null;
  simPolyYesMaker?: { taker: number | null; maker: number | null } | null;
  simPolyNoMaker?: { taker: number | null; maker: number | null } | null;
  simKalshiYesMaker?: { taker: number | null; maker: number | null } | null;
  simKalshiNoMaker?: { taker: number | null; maker: number | null } | null;
  /** Engine-parity sim — see EnrichedEntryStatus.engineSim. */
  engineSim?: EngineSim | null;
  /**
   * The LIVE engine's believed position for this rule. `null` until a live
   * envelope arrives. Powers the per-row "Engine position" drawer.
   */
  believedPosition?: BelievedPosition | null;
  /**
   * The engine-native real-time per-pair ACT + money for this pair's rule.
   * `null` when the pair has no rule or no live `ruleCost` envelope has arrived
   * yet. The ACT cell reads `ruleCost.kalshiNet` / `ruleCost.polyNet` off this.
   */
  ruleCost?: RuleCost | null;
  /**
   * Per-market positions read off the venue by this group's feed pod, under the
   * group's own account. `null` when the pod has no venue source — never zeros.
   * Provenance + freshness ride the separate `venue_positions_meta` event.
   */
  venuePositions?: PairVenuePositions | null;
}

export type MakerFlag =
  | 'polyYesMaker'
  | 'polyNoMaker'
  | 'kalshiYesMaker'
  | 'kalshiNoMaker';

/** Minimal per-path counter shape consumed by the FE — only the
 *  threshold is read/written today. */
export interface PathCounters {
  thresholdSum?: number | null;
}

export interface EntryRule {
  _id: string;
  pairId: string;
  pairName: string;
  expiryDate: string;
  customExpiryDate?: string | null;
  category?: string;
  /**
   * The owning opener group (`entry_rules.openerGroupId`, mirrored from the
   * pair). The single SOURCE OF TRUTH the FE routes a grouped write by — a
   * grouped rule must be written against its group's feed pod, never the core
   * pod (which 409s grouped writes). Read straight off the rule object so
   * routing never depends on cross-joining the volatile `manual-confirmed-pairs`
   * cache (a cache-miss there used to silently route a grouped write to core).
   * `null`/absent = ungrouped → core base.
   */
  openerGroupId?: string | null;
  ruleType?: EntryRuleType;
  /** Pair-level threshold for sum_of_sides rules; the engine fires when
   *  currentSumOfAsks <= thresholdSum AND < ENTRY_HARD_CAP_GROSS. Used as
   *  the create-time seed for both paths; per-path edits live on
   *  `pathA`/`pathB`. */
  thresholdSum?: number | null;
  /** Per-path counters. Each tab (Path A = Poly YES + Kalshi NO, Path B =
   *  Poly NO + Kalshi YES) edits its OWN threshold via these. */
  pathA?: PathCounters | null;
  pathB?: PathCounters | null;
  kalshiConfig: {
    side: 'yes' | 'no';
    ticker: string;
    contractsToBuy: number;
    targetTnaPercent?: number | null;
  } | null;
  polymarketConfig: {
    side: 'yes' | 'no';
    // Both outcome tokens; `tokenId` is the backend virtual derived from
    // `side` (side === 'yes' ? yesTokenId : noTokenId).
    yesTokenId: string;
    noTokenId: string;
    tokenId: string;
    conditionId: string;
    contractsToBuy: number;
    targetTnaPercent?: number | null;
  } | null;
  kalshiEnabled: boolean;
  polymarketEnabled: boolean;
  maxVolumeContracts: number;
  volumePeriod: 'hour' | 'day';
  /**
   * Sizing strategy for the resting maker order. 'fixed' (default) rests at
   * a fixed size equal to the maker leg's `contractsToBuy` (NOT maxOrderSize —
   * the engine's fixed_maker_size reads contractsToBuy); 'dynamic' sizes the
   * maker to the counter platform's top-of-book depth.
   */
  makerSizeMode?: 'dynamic' | 'fixed';
  /**
   * Per-rule counter-hedge pricing mode. `null` (or absent) means "inherit
   * the global `counterHedgeModeDefault`". Otherwise overrides it for this
   * rule. Updated via the existing rule PATCH.
   */
  counterHedgeMode?: 'always-threshold' | 'gtc-to-max-sum' | 'market-to-hedge' | null;
  /**
   * Per-rule opt-in to let an `always-threshold` rule's threshold rest ABOVE
   * $1 (up to 1.4). Default/absent = false → the legacy `< 1.00` hard cap
   * applies. Meaningless for `gtc-to-max-sum` / `market-to-hedge`, where
   * above-$1 is mode-driven. Toggled via
   * `POST /rules/:id/allow-threshold-above-one`.
   */
  allowThresholdAboveOne?: boolean;
  /**
   * Counter-stuck market-hedge timeout (seconds). If the resting counter
   * maker hasn't filled after N seconds the engine cancels it and hedges
   * at market. `null` (or absent) = off — the opener then inherits the
   * group's `counterStuckMarketHedgeSeconds` fallback (both empty = off).
   */
  counterStuckMarketHedgeSeconds?: number | null;
  /**
   * Milliseconds between rungs of the counter/hedge PRICE LADDER: the hedge
   * walks threshold → 1.00 → `marketHedgeMaxSum` instead of resting once at the
   * ceiling. There is no separate toggle: a RESOLVED `null` is the gate. But
   * resolution is `rule ?? group`, so `null` HERE means inherit — only with
   * the group empty too does the counter path go back to the pre-ladder one,
   * and there is no rule-level value that means "this rule does not walk"
   * while its group does. Floor 250 ms — the write DTOs reject a smaller positive
   * value with a 400 and the engine clamps one that still reaches it, so the
   * FE's refusal is what turns that into a red box before the write.
   */
  counterHedgeLadderStepMs?: number | null;
  /**
   * Ticks the maker leg's own book must move for the ladder to skip straight to
   * the max-sum rung (adverse) or raise the appreciation alert (favourable).
   * `null` (or absent) inherits the OPENER-GROUP value first and only then the
   * engine default — which ships at 0 (`COUNTER_LADDER_ADVERSE_TICKS_DEFAULT`),
   * not the 2 the spec asked for. So a blank cell on a rule whose group sets 3
   * runs the adverse skip at 3, and only a blank on BOTH resolves to "branches
   * off". An explicit `0` says that out loud at rule level and leaves a purely
   * timed ladder. Only consulted
   * while `counterHedgeLadderStepMs` resolves to a value. Not to be confused
   * with the engine's existing `adverse_cancel_ticks` (the maker
   * adverse-CANCEL threshold), which is a different knob that also defaults
   * to 2.
   */
  counterHedgeLadderAdverseTicks?: number | null;
  /**
   * Keep posting the Polymarket maker even when the book sits far from the
   * rule's threshold (illiquid markets). `null` (or absent) = off — the opener
   * then inherits the group/global default (all empty = off). Per-platform:
   * Polymarket is the illiquid side where keeping a standing punta is wanted.
   */
  disablePlacementGatePolymarket?: boolean | null;
  /**
   * Same as `disablePlacementGatePolymarket` but for the Kalshi maker leg.
   * Kalshi has more volume + a strict rate limit, so the gate is usually kept
   * ON there (this stays off/null).
   */
  disablePlacementGateKalshi?: boolean | null;
  /**
   * The Polymarket maker leg goes out with `postOnly`: if the resulting price
   * would be marketable the venue REJECTS the order instead of executing it,
   * and the engine reprices on the spot instead of ending up buying as a
   * taker. `null` (or absent) = off — the opener then inherits the
   * group/global default (all empty = off, i.e. today's behavior).
   */
  polyMakerPostOnly?: boolean | null;
  lockstepMaxAheadBatches?: number | null;
  status: EntryRuleStatus;
  contractsBoughtKalshi: number;
  contractsBoughtPolymarket: number;
  totalVolumeExecutedContracts: number;
  activeKalshiOrderId: string | null;
  activePolymarketOrderId: string | null;
  lastExecutionAt: string | null;
  activatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // 4 per-(platform, side) maker toggles. See backend EntryRule schema
  // for the path-exclusivity + position-bought-lock contract.
  polyYesMaker?: boolean;
  polyNoMaker?: boolean;
  kalshiYesMaker?: boolean;
  kalshiNoMaker?: boolean;
  // Entry strategy: 'maker-first' (default) rests a maker on one leg and
  // hedges via FAK taker; 'taker-taker' fires both legs as parallel takers
  // when the combined gross cost (both fees in) clears the threshold and
  // falls back to a balancing maker on any asymmetry.
  entryStrategy?: EntryStrategy;
  // Taker-taker balancing state, engine-managed. Frontend reads them to
  // render the "BAL ←" indicator on the rules table.
  balancingLeg?: 'kalshi' | 'polymarket' | null;
  balancingLegVwap?: number | null;
  balancingContractsRemaining?: number;
  // Out-of-session hedge-check safety net (see hedge-watchdog).
  hedgeCheckEnabled?: boolean;
  hedgeCheckIntervalMs?: number;
  maxUnhedgedContracts?: number | null;
  hedgeCheckConfirmations?: number;
  // ── Sweep response ─────────────────────────────────────────────────────
  // When a large adverse book sweep is detected, run the sweep response
  // (rising-leg-first) instead of the ordinary maker-first flow. All
  // optional; the engine applies its own defaults when omitted. The on/off
  // field keeps its legacy `sweepDualPostEnabled` name (unmigrated).
  sweepDualPostEnabled?: boolean;
  sweepWindowMs?: number;
  sweepMinTicks?: number;
  sweepMinLevelsCleared?: number;
  sweepDepthFraction?: number;
  sweepMinContractsFloor?: number;
  sweepDeltaSigma?: number;
  sweepConfirmMs?: number;
  sweepFreshMs?: number;
  sweepResolveMs?: number;
  sweepKalshiOffsetLevels?: number;
  sweepPolymarketOffsetLevels?: number;
  sweepRefireMinTicks?: number;
  // Adverse cancel — pull the resting maker when the book moves against us.
  adverseCancelEnabled?: boolean;
  adverseCancelTicks?: number;
  liveMakerSizeFactor?: number;
  // ── Forfait protection ─────────────────────────────────────────────────
  // Opt-in per rule (default off = byte-identical behaviour). Config knobs
  // are all nullable: the engine owns the defaults and clamps, so `null`
  // means "inherit", never "0".
  forfaitProtectionEnabled?: boolean;
  forfaitPolyMaxBuyPrice?: number | null;
  forfaitSumFloor?: number | null;
  forfaitConsecutiveCycles?: number | null;
  forfaitCycleWindowMs?: number | null;
  forfaitHoldMs?: number | null;
  forfaitHoldMaxMs?: number | null;
  forfaitSuspendBothPaths?: boolean;
  // Engine-written state — READ-ONLY here (the row's armed badge). Never
  // sent back in a create/update DTO.
  forfaitArmedAt?: string | null;
  forfaitTrigger?: ForfaitTrigger | null;
  forfaitSuspendedPaths?: ('a' | 'b')[];
  forfaitTripSnapshot?: ForfaitTripSnapshot | null;
}

/** Which detector armed the regime (wire vocabulary, frozen).
 *  `pin_divergence` is the RETIRED price-only detector — nothing writes it any
 *  more, but rules it armed before its removal still carry it. */
export type ForfaitTrigger = 'pin_divergence' | 'consecutive_cycles';

/** The prices and counters at the moment the regime armed. */
export interface ForfaitTripSnapshot {
  polyMid: number;
  kalshiMid: number;
  sumGross: number | null;
  cycles: number;
}

export interface EntryExecution {
  _id: string;
  ruleId: string;
  pairId: string;
  pairName: string;
  direction: 'kalshi_maker' | 'polymarket_maker';
  makerLeg: {
    orderId: string;
    platform: string;
    side: string;
    price: number;
    contracts: number;
    fee: number;
    status: string;
  };
  takerLeg: {
    orderId: string;
    platform: string;
    side: string;
    price: number;
    contracts: number;
    fee: number;
    orderType: string;
    status: string;
  };
  totalVolumeUsd: number;
  effectiveTnaPercent: number;
  capitalDeployed: number;
  overallStatus:
    | 'complete'
    | 'partial'
    | 'maker_only'
    | 'failed'
    | 'pending_counter';
  errorMessage?: string;
  createdAt: string;
}

export interface CreateEntryRuleDto {
  pairId: string;
  pairName: string;
  expiryDate: string;
  customExpiryDate?: string | null;
  ruleType?: EntryRuleType;
  thresholdSum?: number;
  pathAThresholdSum?: number;
  pathBThresholdSum?: number;
  kalshiConfig?: {
    side: 'yes' | 'no';
    ticker: string;
    contractsToBuy: number;
    targetTnaPercent?: number;
  };
  polymarketConfig?: {
    side: 'yes' | 'no';
    // Both outcome tokens are sent up front; the backend derives the
    // active token from `side`, so a path flip never needs a token edit.
    yesTokenId: string;
    noTokenId: string;
    conditionId: string;
    contractsToBuy: number;
    targetTnaPercent?: number;
  };
  maxVolumeContracts: number;
  volumePeriod: 'hour' | 'day';
  kalshiEnabled?: boolean;
  polymarketEnabled?: boolean;
  polyYesMaker?: boolean;
  polyNoMaker?: boolean;
  kalshiYesMaker?: boolean;
  kalshiNoMaker?: boolean;
  entryStrategy?: EntryStrategy;
  makerSizeMode?: 'dynamic' | 'fixed';
  counterHedgeMode?:
    | 'always-threshold'
    | 'gtc-to-max-sum'
    | 'market-to-hedge'
    | null;
  counterStuckMarketHedgeSeconds?: number | null;
  /** See `EntryRule.counterHedgeLadderStepMs`. `null` clears the per-rule
   *  override back to the group/subgroup fallback. */
  counterHedgeLadderStepMs?: number | null;
  /** See `EntryRule.counterHedgeLadderAdverseTicks`. */
  counterHedgeLadderAdverseTicks?: number | null;
  disablePlacementGatePolymarket?: boolean | null;
  disablePlacementGateKalshi?: boolean | null;
  /** See `EntryRule.polyMakerPostOnly`. */
  polyMakerPostOnly?: boolean | null;
  lockstepMaxAheadBatches?: number | null;
  hedgeCheckEnabled?: boolean;
  hedgeCheckIntervalMs?: number;
  maxUnhedgedContracts?: number;
  hedgeCheckConfirmations?: number;
  sweepDualPostEnabled?: boolean;
  sweepWindowMs?: number;
  sweepMinTicks?: number;
  sweepMinLevelsCleared?: number;
  sweepDepthFraction?: number;
  sweepMinContractsFloor?: number;
  sweepDeltaSigma?: number;
  sweepConfirmMs?: number;
  sweepFreshMs?: number;
  sweepResolveMs?: number;
  sweepKalshiOffsetLevels?: number;
  sweepPolymarketOffsetLevels?: number;
  sweepRefireMinTicks?: number;
  adverseCancelEnabled?: boolean;
  adverseCancelTicks?: number;
  liveMakerSizeFactor?: number;
  forfaitProtectionEnabled?: boolean;
  forfaitPolyMaxBuyPrice?: number | null;
  forfaitSumFloor?: number | null;
  forfaitConsecutiveCycles?: number | null;
  forfaitCycleWindowMs?: number | null;
  forfaitHoldMs?: number | null;
  forfaitHoldMaxMs?: number | null;
  forfaitSuspendBothPaths?: boolean;
  /** Per-pair "> $1" opt-in. Accepted at create (the backend spreads it onto
   *  the new rule); mutated afterwards ONLY via the dedicated action. */
  allowThresholdAboveOne?: boolean;
}

export interface UpdateEntryRuleDto {
  /**
   * Explicit arm/disarm intent (spec D5). When present this is the ONLY thing
   * that arms (`true`) or pauses (`false`) the rule; the backend no longer
   * infers arming from a maker-flag transition or a bare params update. Absent
   * → the update touches only params/config and never changes armed state.
   */
  desiredArmed?: boolean;
  thresholdSum?: number;
  pathAThresholdSum?: number;
  pathBThresholdSum?: number;
  kalshiConfig?: {
    side?: 'yes' | 'no';
    ticker: string;
    contractsToBuy: number;
    targetTnaPercent?: number;
  };
  polymarketConfig?: {
    side?: 'yes' | 'no';
    yesTokenId: string;
    noTokenId: string;
    conditionId: string;
    contractsToBuy: number;
    targetTnaPercent?: number;
  };
  maxVolumeContracts?: number;
  volumePeriod?: 'hour' | 'day';
  customExpiryDate?: string | null;
  polyYesMaker?: boolean;
  polyNoMaker?: boolean;
  kalshiYesMaker?: boolean;
  kalshiNoMaker?: boolean;
  entryStrategy?: EntryStrategy;
  makerSizeMode?: 'dynamic' | 'fixed';
  counterHedgeMode?: 'always-threshold' | 'gtc-to-max-sum' | 'market-to-hedge' | null;
  counterStuckMarketHedgeSeconds?: number | null;
  /** See `EntryRule.counterHedgeLadderStepMs`. `null` clears the per-rule
   *  override back to the group/subgroup fallback. */
  counterHedgeLadderStepMs?: number | null;
  /** See `EntryRule.counterHedgeLadderAdverseTicks`. */
  counterHedgeLadderAdverseTicks?: number | null;
  disablePlacementGatePolymarket?: boolean | null;
  disablePlacementGateKalshi?: boolean | null;
  /** See `EntryRule.polyMakerPostOnly`. */
  polyMakerPostOnly?: boolean | null;
  lockstepMaxAheadBatches?: number | null;
  hedgeCheckEnabled?: boolean;
  hedgeCheckIntervalMs?: number;
  maxUnhedgedContracts?: number;
  hedgeCheckConfirmations?: number;
  sweepDualPostEnabled?: boolean;
  sweepWindowMs?: number | null;
  sweepMinTicks?: number | null;
  sweepMinLevelsCleared?: number | null;
  sweepDepthFraction?: number | null;
  sweepMinContractsFloor?: number | null;
  sweepDeltaSigma?: number | null;
  sweepConfirmMs?: number | null;
  sweepFreshMs?: number | null;
  sweepResolveMs?: number | null;
  sweepKalshiOffsetLevels?: number | null;
  sweepPolymarketOffsetLevels?: number | null;
  sweepRefireMinTicks?: number | null;
  adverseCancelEnabled?: boolean;
  adverseCancelTicks?: number | null;
  liveMakerSizeFactor?: number | null;
  forfaitProtectionEnabled?: boolean;
  forfaitPolyMaxBuyPrice?: number | null;
  forfaitSumFloor?: number | null;
  forfaitConsecutiveCycles?: number | null;
  forfaitCycleWindowMs?: number | null;
  forfaitHoldMs?: number | null;
  forfaitHoldMaxMs?: number | null;
  forfaitSuspendBothPaths?: boolean;
}

/**
 * A pair surfaced by the pair-manager as `manual-confirmed` and `active`
 * — these are the only pairs eligible for entry rule creation. Schema is
 * kept loose because the pair-manager's `manual-confirmed` endpoint is
 * the canonical source: extra fields just pass through.
 */
export interface ManualConfirmedPair {
  _id: string;
  pairName: string;
  polymarketTitle?: string;
  kalshiTitle?: string;
  expiryDate?: string | null;
  category?: string | null;
  subcategory?: string | null;
  /**
   * Soccer order bucket (`prediction_pairs.marketType`), classified from the
   * markets at match time. Drives the opener table's "Market type" sort. Unset
   * for non-soccer pairs.
   */
  marketType?: string | null;
  openerGroupId?: string | null;
  /**
   * The pair's subgroup within its opener group (`prediction_pairs.openerSubgroupId`).
   * null = unassigned. Always points to a subgroup whose `groupId` matches
   * `openerGroupId`; a group reassign nulls it (backend invariant).
   */
  openerSubgroupId?: string | null;
  /**
   * Persisted per-pair opener kill switch (`prediction_pairs.openerEnabled`).
   * Read by the maker-flag arm path to decide whether to auto-enable the pair
   * before arming a leg — the backend rejects arming a leg on an
   * `openerEnabled: false` pair (`assertPairOpenerEnabled` → 400). Default
   * false on newly manual-confirmed pairs.
   */
  openerEnabled?: boolean;
  status?: string;
  archived?: boolean;
  /**
   * Kalshi market settlement (`prediction_pairs.settledOnKalshi`), stamped by
   * the live `market_lifecycle_v2` feed when the Kalshi leg is DETERMINED.
   * When true the start-time cell renders a gray `TERMINADO` chip in place of
   * the green `LIVE` pill, while the pair stays visible. Display-only and
   * independent of `eventFinished` (which removes the pair from the opener).
   */
  settledOnKalshi?: boolean;
  /** Timestamp of the Kalshi determination (null until determined). */
  settledAt?: string | null;
  /** The Kalshi market's settled result, null until determined. */
  kalshiResult?: 'yes' | 'no' | null;
  /**
   * Resolved event kickoff (ISO) for a match-related row, derived in
   * `adaptPair` (Polymarket startDate preferred over Kalshi's). Drives the
   * start-time line + `LIVE` pill and the `startTime` sort. null ⇒ not a
   * match-related row (render no line).
   */
  eventStartTime?: string | null;
  /**
   * Stable per-match grouping key, identical for every market of one match.
   * Drives the `event` group-sort. Derived in `adaptPair`.
   */
  eventKey?: string | null;
  // Polymarket leg identifiers (mirrored to entry rule polymarketConfig)
  polymarket?: {
    yesTokenId?: string;
    noTokenId?: string;
    conditionId?: string;
    side?: 'yes' | 'no';
    marketUrl?: string;
  } | null;
  // Kalshi leg identifiers (mirrored to entry rule kalshiConfig)
  kalshi?: {
    ticker?: string;
    side?: 'yes' | 'no';
    marketUrl?: string;
  } | null;
}

export interface NormalizedOrder {
  id: string;
  platform: 'kalshi' | 'polymarket';
  marketId: string;
  marketTitle?: string;
  marketUrl?: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  price: number;
  quantity: number;
  filledQuantity: number;
  remainingQuantity: number;
  status: string;
  isEntry: boolean;
  ruleId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrdersSummary {
  kalshi: { count: number; totalVolume: number };
  polymarket: { count: number; totalVolume: number };
}

export interface EntryOrdersResponse {
  orders: NormalizedOrder[];
  summary: OrdersSummary;
}
